import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface WeekNavigatorProps {
  currentPeriodStart: string; // YYYY-MM-DD
  weekStartDay: number;       // 0=Sun, 1=Mon, ...
  onPeriodChange: (periodStart: string) => void;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function computeWeekStart(date: Date, weekStartDay: number): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const current = d.getUTCDay();
  const diff = (current - weekStartDay + 7) % 7;
  d.setUTCDate(d.getUTCDate() - diff);
  return d;
}

function parseDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function formatPeriodLabel(periodStart: string): string {
  const start = parseDateKey(periodStart);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);

  const startStr = `${MONTH_NAMES[start.getUTCMonth()]} ${start.getUTCDate()}`;
  const endStr =
    start.getUTCFullYear() === end.getUTCFullYear()
      ? `${MONTH_NAMES[end.getUTCMonth()]} ${end.getUTCDate()}`
      : `${MONTH_NAMES[end.getUTCMonth()]} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  return `${startStr} – ${endStr}, ${start.getUTCFullYear()}`;
}

export default function WeekNavigator({ currentPeriodStart, weekStartDay, onPeriodChange }: WeekNavigatorProps) {
  const today = new Date();
  const thisWeekStart = formatDateKey(computeWeekStart(today, weekStartDay));
  const isCurrentWeek = currentPeriodStart === thisWeekStart;

  const navigate = (direction: -1 | 1) => {
    const start = parseDateKey(currentPeriodStart);
    start.setUTCDate(start.getUTCDate() + direction * 7);
    onPeriodChange(formatDateKey(start));
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-white/60 hover:text-white"
        onClick={() => navigate(-1)}
        aria-label="Previous week"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>

      <span className="text-xs font-medium text-white/80 min-w-[200px] text-center">
        {formatPeriodLabel(currentPeriodStart)}
      </span>

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-white/60 hover:text-white"
        onClick={() => navigate(1)}
        aria-label="Next week"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>

      {!isCurrentWeek && (
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-white/50 hover:text-white h-7 px-2"
          onClick={() => onPeriodChange(thisWeekStart)}
        >
          This Week
        </Button>
      )}
    </div>
  );
}

export { DAY_NAMES, formatDateKey, parseDateKey, computeWeekStart };
